// PATH: src/pages/CoveragePage.tsx
import React from "react";
import { useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/RefreshRounded";
import SearchIcon from "@mui/icons-material/SearchRounded";
import InsightsIcon from "@mui/icons-material/InsightsRounded";
import type { CurriculumProgram } from "../models/domain";
import { useCoverageApi, useCurriculumApi, type CoverageItem, type OssatureChapterItem } from "../api";
import { PageHeader, EmptyState, errorMessage } from "../components/ui";
import CoverageSummary, { Legend } from "../components/coverage/CoverageSummary";
import CoverageMatrixTable from "../components/coverage/CoverageMatrixTable";
import CoverageCellDrawer from "../components/coverage/CoverageCellDrawer";
import {
  buildCoverageMatrix,
  catalogTotals,
  missingOssatureBySubject,
  reviewTotals,
  type MatrixCell,
} from "../components/coverage/coverage";
import { issueTotals, issuesByCell } from "../components/coverage/chapterIssues";

const PROGRAM_KEY = "tc_embed_ui.program";
const readStored = () => {
  try {
    return window.localStorage.getItem(PROGRAM_KEY) || "";
  } catch {
    return "";
  }
};
const store = (id: string) => {
  try {
    window.localStorage.setItem(PROGRAM_KEY, id);
  } catch {
    /* stockage indisponible : on oublie, sans gêne */
  }
};

const normalize = (s: string) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/**
 * Couverture d'un programme rapportée à son catalogue : chaque offre
 * (matière × niveau × filière) est couverte, partielle, à rédiger ou sans
 * ossature — en chiffres, par étape, puis matière × niveau. Une case s'ouvre
 * sur la liste de ses chapitres.
 */
const CoveragePage: React.FC = () => {
  const { getCurriculumOverview } = useCurriculumApi();
  const { getCoverage, listOssatureChapters } = useCoverageApi();
  const [params, setParams] = useSearchParams();

  const [catalog, setCatalog] = React.useState<CurriculumProgram[] | null>(null);
  const [items, setItems] = React.useState<CoverageItem[]>([]);
  /* Chapitres du programme (pour les « à compléter ») : null = non chargés ou
     en échec — la page marche alors comme avant, sans ces marqueurs. */
  const [chapters, setChapters] = React.useState<OssatureChapterItem[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState("");
  const [openCell, setOpenCell] = React.useState<MatrixCell | null>(null);

  const programId = params.get("program") || "";
  const stageFilter = params.get("stage") || "";
  const onlyIncomplete = params.get("incomplete") === "1";

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  // Catalogue : programmes, ordre des étapes/niveaux/matières.
  React.useEffect(() => {
    getCurriculumOverview()
      .then(({ data }) => setCatalog(data?.programs ?? []))
      .catch((e) => {
        setCatalog([]);
        setError(errorMessage(e, "Impossible de charger le catalogue."));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Programme par défaut : celui de l'URL, sinon le dernier consulté, sinon celui qui a une ossature… le premier.
  React.useEffect(() => {
    if (!catalog?.length || programId) return;
    const stored = readStored();
    const pick = catalog.find((p) => p.program.id === stored) ?? catalog[0];
    const next = new URLSearchParams(params);
    next.set("program", pick.program.id);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, programId]);

  const load = React.useCallback(async () => {
    if (!programId) return;
    store(programId);
    setLoading(true);
    setError(null);
    const chaptersReq = listOssatureChapters({ program_id: programId })
      .then(({ data }) => data?.items ?? null)
      .catch(() => null);
    try {
      const [{ data }, list] = await Promise.all([getCoverage(programId), chaptersReq]);
      setItems(data?.items ?? []);
      setChapters(list);
    } catch (e) {
      setItems([]);
      setChapters(null);
      setError(errorMessage(e, "Impossible de charger la couverture."));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const program = catalog?.find((p) => p.program.id === programId) ?? null;
  const matrix = React.useMemo(() => buildCoverageMatrix(items, program), [items, program]);
  const stages = stageFilter ? matrix.stages.filter((s) => s.id === stageFilter) : matrix.stages;
  const visibleGradeIds = React.useMemo(() => new Set(stages.flatMap((s) => s.grades.map((g) => g.id))), [stages]);
  const visibleItems = React.useMemo(() => items.filter((it) => visibleGradeIds.has(it.grade_id)), [items, visibleGradeIds]);
  const totals = React.useMemo(() => catalogTotals(visibleItems), [visibleItems]);
  const missing = React.useMemo(() => missingOssatureBySubject(matrix, Array.from(visibleGradeIds)), [matrix, visibleGradeIds]);
  const visibleChapters = React.useMemo(
    () => (chapters ? chapters.filter((c) => visibleGradeIds.has(c.grade_id)) : null),
    [chapters, visibleGradeIds],
  );
  const issues = React.useMemo(() => (visibleChapters ? issueTotals(visibleChapters) : null), [visibleChapters]);
  const cellIssues = React.useMemo(() => (chapters ? issuesByCell(chapters) : null), [chapters]);
  const issuesLink = `/chapters?${new URLSearchParams({ program: programId, issue: "any" }).toString()}`;
  const review = React.useMemo(() => reviewTotals(visibleItems), [visibleItems]);
  const reviewLink = `/chapters?${new URLSearchParams({ program: programId, review: "a_relire" }).toString()}`;

  const needle = normalize(query.trim());
  const subjectFilter = React.useCallback((name: string) => !needle || normalize(name).includes(needle), [needle]);

  const subjectName = openCell ? matrix.subjects.find((s) => s.id === openCell.subjectId)?.name ?? "" : "";
  const gradeName = openCell
    ? matrix.stages.flatMap((s) => s.grades).find((g) => g.id === openCell.gradeId)?.name ?? ""
    : "";

  return (
    <Box>
      <PageHeader
        title="Couverture"
        subtitle="Où en est le programme, offre par offre du catalogue : ossature publiée, chapitres importés, puis jouables par les élèves."
        actions={
          <>
            <TextField
              select
              value={programId}
              onChange={(e) => {
                setOpenCell(null);
                const next = new URLSearchParams();
                next.set("program", e.target.value);
                setParams(next, { replace: true });
              }}
              sx={{ minWidth: 240 }}
              disabled={!catalog?.length}
              slotProps={{ select: { displayEmpty: true } }}
            >
              {!catalog && <MenuItem value="">Chargement…</MenuItem>}
              {(catalog ?? []).map((p) => (
                <MenuItem key={p.program.id} value={p.program.id}>
                  {p.program.name}
                </MenuItem>
              ))}
            </TextField>
            <Tooltip title="Actualiser">
              <span>
                <IconButton onClick={() => void load()} disabled={loading || !programId} sx={{ border: 1, borderColor: "divider", bgcolor: "background.paper" }}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </>
        }
      />

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void load()}>
              Réessayer
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <CoverageSummary
        totals={totals}
        matrix={{ ...matrix, stages }}
        missing={missing}
        loading={loading}
        issues={issues}
        issuesLink={issuesLink}
        review={review}
        reviewLink={reviewLink}
      />

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
          <Chip
            label="Toutes les étapes"
            color={!stageFilter ? "primary" : "default"}
            variant={!stageFilter ? "filled" : "outlined"}
            onClick={() => setParam("stage", "")}
          />
          {matrix.stages
            .filter((s) => s.id)
            .map((s) => (
              <Chip
                key={s.id}
                label={s.name}
                color={stageFilter === s.id ? "primary" : "default"}
                variant={stageFilter === s.id ? "filled" : "outlined"}
                onClick={() => setParam("stage", stageFilter === s.id ? "" : s.id)}
              />
            ))}
          {cellIssues && (
            <Tooltip title="Estompe les cases dont tous les chapitres importés sont complets">
              <Chip
                label={issues ? `À compléter seulement (${issues.incomplete})` : "À compléter seulement"}
                color={onlyIncomplete ? "error" : "default"}
                variant={onlyIncomplete ? "filled" : "outlined"}
                onClick={() => setParam("incomplete", onlyIncomplete ? "" : "1")}
              />
            </Tooltip>
          )}
          <TextField
            placeholder="Filtrer les matières"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            sx={{ width: { xs: "100%", sm: 220 } }}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
        <Legend />
      </Stack>

      {loading ? (
        <Card sx={{ p: 2 }}>
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} height={44} />
          ))}
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<InsightsIcon />}
            title="Rien à mesurer pour ce programme"
            description="Le catalogue n'offre aucune matière à aucun niveau, et rien n'est publié."
          />
        </Card>
      ) : (
        <CoverageMatrixTable
          matrix={matrix}
          stages={stages}
          subjectFilter={subjectFilter}
          onOpenCell={setOpenCell}
          cellIssues={cellIssues}
          onlyIncomplete={onlyIncomplete}
        />
      )}

      <CoverageCellDrawer
        programId={programId}
        cell={openCell}
        programChapters={chapters}
        subjectName={subjectName}
        gradeName={gradeName}
        onClose={() => setOpenCell(null)}
      />
    </Box>
  );
};

export default CoveragePage;
